import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  REPORT_PERIODS,
  ReportPeriodDays,
} from '../../../../services/reports/report-period';

/**
 * "Last 7 / 30 / 90 days" as one segmented switch. A radio group: arrow keys
 * move between periods, and the chosen one carries the inverted fill.
 */
@Component({
  selector: 'report-period',
  standalone: true,
  template: `
    <div class="seg" role="radiogroup" [attr.aria-label]="label" (keydown)="onKeydown($event)">
      <span class="seg__lead" aria-hidden="true">Last</span>
      @for (option of periods; track option.days) {
        <button
          type="button"
          role="radio"
          class="seg__opt"
          [class.is-on]="option.days === value"
          [attr.aria-checked]="option.days === value"
          [attr.tabindex]="option.days === value ? 0 : -1"
          [disabled]="disabled"
          (click)="choose(option.days)"
        >
          {{ option.label }}
        </button>
      }
    </div>
  `,
  styles: `
    :host {
      display: inline-flex;
      min-width: 0;
    }

    .seg {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      height: 40px;
      padding: 3px;
      border: 1px solid var(--bk-line);
      border-radius: var(--bk-r-pill);
      background: var(--bk-panel);
      font-family: var(--bk-font);
    }

    .seg__lead {
      padding: 0 8px 0 12px;
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--bk-faint);
    }

    .seg__opt {
      height: 32px;
      padding: 0 14px;
      border: none;
      border-radius: var(--bk-r-pill);
      background: transparent;
      font-family: var(--bk-font);
      font-size: 12.5px;
      font-weight: 700;
      white-space: nowrap;
      color: var(--bk-ash);
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      transition:
        background-color 160ms ease,
        color 160ms ease,
        transform 160ms var(--bk-ease-out);
    }

    .seg__opt:active {
      transform: scale(0.97);
    }

    .seg__opt:focus-visible {
      outline: 2px solid var(--bk-ink);
      outline-offset: 1px;
    }

    .seg__opt.is-on {
      background: var(--bk-invert);
      color: var(--bk-on-invert);
    }

    .seg__opt:disabled {
      cursor: progress;
    }

    @media (hover: hover) and (pointer: fine) {
      .seg__opt:not(.is-on):hover {
        background: var(--bk-hover);
        color: var(--bk-ink);
      }
    }

    @media (max-width: 767px) {
      :host,
      .seg {
        width: 100%;
      }

      .seg__opt {
        flex: 1 1 0;
        padding: 0 8px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .seg__opt {
        transition-property: background-color, color;
      }

      .seg__opt:active {
        transform: none;
      }
    }
  `,
})
export class ReportPeriodComponent {
  @Input() value: ReportPeriodDays = 30;
  @Input() label = 'Reporting period';
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<ReportPeriodDays>();

  readonly periods = REPORT_PERIODS;

  choose(days: ReportPeriodDays) {
    if (days === this.value) return;
    this.value = days;
    this.valueChange.emit(days);
  }

  onKeydown(event: Event) {
    const key = (event as KeyboardEvent).key;
    const step = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : key === 'ArrowLeft' || key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const index = this.periods.findIndex((option) => option.days === this.value);
    const next = this.periods[(index + step + this.periods.length) % this.periods.length];
    this.choose(next.days);
    const group = event.currentTarget as HTMLElement | null;
    setTimeout(() => group?.querySelector<HTMLButtonElement>('.seg__opt.is-on')?.focus());
  }
}
